import React, { FC, useRef, useState } from 'react';
import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { ORDER_STATUS } from '@baemingo/order-status-events/dist/types';
import ErrorNote from '~Components/ErrorNote';
import { useRefunds } from '~Components/Refunds/Provider';
import { PAYMENT_TYPE } from '~Components/Refunds/Provider/types';
import { useStorage } from '~Components/Storage';
import Modal from '~ComponentsImproved/Modal';
import ModalWrapper from '~ComponentsImproved/Modal/ModalWrapper';
import Loader from '~ComponentsImproved/Loader';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { useButton, useDispatch, useI18n, useLicenses, useLogger, useStaffCan } from '~Hooks';
import { isSameBusinessDay } from '~Hooks/Payments/useHotelRoomPayment/hotel-room';
import useConfirm from '~Hooks/useConfirm';
import { CopyToTablePicker } from '~Scenes/History/Components/CopyToTable';
import { toRefundItems } from '~Scenes/Refunds/utils';
import { GetTables_Venue_floorPlans_tables as TableType } from '~Scenes/Venue/__queries__';
import { BlackButton, Box, Button } from '~Styles';
import * as actions from '~actions';
import { GetOrder_order } from '../__queries__';

/* Same size as "Copy to table", which it sits next to */
const ReopenButton = styled(Button)`
  min-height: 32px;
  font-size: 12px;
  padding: 8px 16px;
`;

/**
 * The reopen runs in two stages: the payment method corrects the charge in the
 * hotel's PMS, then the order is refunded in Baemingo. The spinner's text names
 * the running one, and a failure says which one failed.
 */
type Stage = 'PMS' | 'POS';

type Step =
  | { kind: 'IDLE' }
  | { kind: 'RUNNING'; stage: Stage }
  | { kind: 'FAILED'; stage: Stage; message: string }
  | { kind: 'TABLES' };

/* Nearly square, so the spinner and its text sit in the middle */
const Content = styled(Box)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 24px;
  text-align: center;
`;

const pulse = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.4; }
  100% { opacity: 1; }
`;

/* Shows that the reopen is still working while the text stays the same */
const PulsingText = styled(Box)`
  animation: ${pulse} 1.6s ease-in-out infinite;
`;

interface Props {
  order: GetOrder_order;
  onRefunded: () => void;
  onHandyComplete?: (table: TableType, chequeId: string) => void;
}

/**
 * Reopens an order that was paid to a hotel room (requirement 2.10). A closed
 * receipt can only be undone with a refund, so this refunds the whole order,
 * which takes the charge back from the hotel as a reopen, and then copies the
 * items to a table. When that cheque is sent to a room again, the hotel is
 * told which charge it replaces, so its event log shows the reopen.
 *
 * Nothing here can be closed by tapping outside: once the order is refunded,
 * the table picker is the only way back to its items.
 */
const ReopenOrder: FC<Props> = ({ order, onRefunded, onHandyComplete }) => {
  const { i18n } = useI18n();
  const logger = useLogger('history:reopen');
  const dispatch = useDispatch();
  const refunds = useRefunds();
  const staffCan = useStaffCan();
  const licenses = useLicenses();
  const { confirm } = useConfirm();
  const [storage, updateStorage] = useStorage();
  const [step, setStep] = useState<Step>({ kind: 'IDLE' });
  /* The callbacks below outlive the render that created them, so they read the stage from here */
  const stage = useRef<Stage>('PMS');

  const chargeId = order.payment.transactionId;
  const nothingRefunded =
    order.status === ORDER_STATUS.COMPLETED && order.items.every((item) => !item.isRefunded);
  const sameDay = isSameBusinessDay(order.payment.createdAt);

  const enterStage = (next: Stage) => {
    stage.current = next;
    setStep({ kind: 'RUNNING', stage: next });
  };

  const reopen = () => {
    logger.info('Reopening hotel room order', { orderId: order.id, chargeId });
    enterStage('PMS');

    refunds.add({
      type: 'ORDER',
      reopen: true,
      method: order.payment.type,
      orderId: order.id,
      origin: order.origin,
      transactionCreatedAt: order.payment.createdAt,
      transactionId: chargeId,
      waiterId: order.staff?.id ?? '',
      tipAmount: order.payment.correctedTip ?? 0,
      items: toRefundItems(order.items, i18n),
      onPaymentRefunded: () => {
        logger.info('Hotel room charge taken back for reopen', { orderId: order.id, chargeId });
        enterStage('POS');
      },
      onCompleted: (receiptError?: string) => {
        logger.info('Hotel room order refunded for reopen', { orderId: order.id, receiptError });
        if (receiptError) {
          dispatch(
            actions.addToast(
              i18n('History.RefundSuccessful') + ' - ' + i18n('History.ReceiptFailed'),
              true,
            ),
          );
        }
        onRefunded();
        setStep({ kind: 'TABLES' });
      },
      onFail: (error?: string) => {
        const failedStage = stage.current;
        logger.error('Hotel room order could not be reopened', {
          orderId: order.id,
          chargeId,
          failedStage,
          errorMessage: error,
        });
        setStep({
          kind: 'FAILED',
          stage: failedStage,
          message:
            failedStage === 'PMS'
              ? error || i18n('HotelRoom.Error')
              : i18n('History.Reopen.Failed.Pos'),
        });
        if (failedStage === 'POS') {
          // The hotel was corrected but the order was not refunded; the page must show that
          onRefunded();
        }
      },
    });
  };

  /* Reopening refunds the order, so a misclick must not start it */
  const confirmReopen = async () => {
    const confirmed = await confirm(
      i18n('History.Reopen.Confirm.Title'),
      undefined,
      i18n('History.Reopen.Confirm.Subtitle'),
    ).catch(() => false);
    if (!confirmed) {
      logger.info('Reopen cancelled', { orderId: order.id, chargeId });
      return;
    }
    reopen();
  };

  const reopenButton = useButton({
    onClick: confirmReopen,
    disabled: !staffCan.REFUND || !nothingRefunded || !sameDay || refunds.hasRefundInProgress,
    disabledToast: !staffCan.REFUND
      ? i18n('Refunds.Disabled.StaffNotAllowed')
      : !nothingRefunded
      ? i18n('History.Reopen.Disabled.Refunded')
      : !sameDay
      ? i18n('History.Reopen.Disabled.NotSameDay')
      : refunds.hasRefundInProgress
      ? i18n('History.Reopen.Disabled.InProgress')
      : undefined,
  });

  const closeButton = useButton({
    onClick: () => setStep({ kind: 'IDLE' }),
  });

  /* The reopened cheque goes to a table, so there is nothing to reopen into without TSR */
  if (order.payment.type !== PAYMENT_TYPE.HOTEL_ROOM || !licenses.hasTSR) {
    return null;
  }

  const easyId = order.easyId ?? '';
  const isHandy = window.innerWidth <= 600;

  return (
    <>
      <ReopenButton {...reopenButton}>{i18n('History.Reopen.Button')}</ReopenButton>

      <Modal
        isShowing={step.kind === 'RUNNING' || step.kind === 'FAILED'}
        hide={() => undefined}
        backdropClose={false}
        position="center">
        <ModalWrapper
          width="400px"
          isHandy={isHandy}
          header={
            step.kind === 'FAILED' ? { title: i18n('History.Reopen.Failed.Title') } : undefined
          }>
          <Content>
            {step.kind === 'RUNNING' && (
              <>
                <Loader />
                <PulsingText>
                  <Paragraph level={3} color="var(--colorWhite)">
                    {step.stage === 'PMS'
                      ? i18n('History.Reopen.Step.Pms', easyId)
                      : i18n('History.Reopen.Step.Pos', easyId)}
                  </Paragraph>
                </PulsingText>
              </>
            )}

            {step.kind === 'FAILED' && (
              <>
                <ErrorNote>{step.message}</ErrorNote>
                <Paragraph level={4} color="var(--colorBlueGray500)">
                  {step.stage === 'PMS'
                    ? i18n('History.Reopen.Failed.NothingChanged')
                    : `${i18n('History.Order')} #${easyId}`}
                </Paragraph>
                <BlackButton {...closeButton}>{i18n('History.Close')}</BlackButton>
              </>
            )}
          </Content>
        </ModalWrapper>
      </Modal>

      <CopyToTablePicker
        order={order}
        isShowing={step.kind === 'TABLES'}
        hide={() => setStep({ kind: 'IDLE' })}
        closable={false}
        onHandyComplete={onHandyComplete}
        onCopied={(chequeId) => {
          logger.info('Reopened order copied to table', { orderId: order.id, chargeId, chequeId });
          const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
          updateStorage({
            hotelReopenedCheques: [
              ...(storage.hotelReopenedCheques ?? []).filter(
                (entry) => Date.parse(entry.createdAt) > weekAgo,
              ),
              { chequeId, reopenedFrom: chargeId, createdAt: new Date().toISOString() },
            ],
          });
        }}
      />
    </>
  );
};

export default ReopenOrder;
